/* eslint-disable prettier/prettier */
import { TreatyEntityType } from '../types/TreatyEntityType';
import { api } from './axios';

export const FileService = {
  uploadFile: async (file: File, title?: string, description?: string, folderId?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('editedTitle', title);
    if (description) formData.append('description', description);
    if (folderId) formData.append('folderId', folderId);

    try {
      const response = await api.post('/files/upload', formData, {
        onUploadProgress: () => {}, 
        responseType: 'json',
      });
      return response.data;
    } catch (e: any) {
      if (e?.response) {
        const msg =
          typeof e.response.data?.message === 'string'
            ? e.response.data.message
            : e.response.data?.message?.message || `Server error (${e.response.status}).`;
        throw new Error(msg);
      }
      throw new Error(e?.message || 'Network error');
    }
  },
  getFiles: () => api.get('/files/latest-version'),

  getAllFiles: () => api.get('/files'),

  editFile(
    fileId: string,
    data: { editedTitle?: string; description?: string; folderId?: string },
  ) {
    return api.patch(`/files/${fileId}`, data);
  },

  deleteFile: (id: string) => api.delete(`/files/${id}`),

  getFile: (id: string) => api.get(`/files/${id}`),

  getTreatyByFileId: (fileId: string) => {
    return api.get(`/treaty/by-file/${fileId}`);
  },

  getPdfByFileId: (fileId: string) => {
    return api.get(`/files/pdf/${fileId}`, {
      responseType: 'blob',
    });
  },

  updateField(payload: {
    entity: TreatyEntityType;
    entityId: string;
    field: string;
    newValue: any;
  }) {
    return api.patch('/treaty/update-field', payload);
  },

  resetField(payload: { entity: TreatyEntityType; entityId: string; field: string }) {
    return api.patch('/treaty/reset-field', payload);
  },

  async checkDuplicateFileName(name: string): Promise<boolean> {
    const res = await api.get('/files/check-name', { params: { name } });
    return res.data.exists;
  },

  getVersionsChain: () => api.get('/treaty/version-chains'),

  getComparePYGroups: () => api.get('/treaty/compare-py-groups'),

  compareVersions: (leftId: string, rightId: string) =>
    api.get('/treaty/compare-versions', {
      params: {
        leftId,
        rightId,
      },
    }),

  getCedants: () => api.get('/cedants'),

  updateCedantInTreaty: async (data: {
    treatyId: string;
    oldCedantId: string;
    newCedantId: string;
  }) => {
    try {
      const response = await api.patch(`/treaty/${data.treatyId}/update-cedant`, {
        oldCedantId: data.oldCedantId,
        newCedantId: data.newCedantId,
      });
      return response.data;
    } catch (error) {
      console.error('Failed to update cedant in treaty:', error);
      throw error;
    }
  },

  addCedantToTreaty: async (treatyId: string, cedantId: string) => {
    return api.patch(`/treaty/${treatyId}/add-cedant`, { cedantId });
  },

  removeCedantFromTreaty: async (treatyId: string, cedantId: string) => {
    return api.patch(`/treaty/${treatyId}/remove-cedant`, { cedantId });
  },

  triggerIngestion: async () => {
    return api.post('/mail/ingest');
  },

  deleteMultipleFiles: (ids: string[]) => {
    return api.delete('/files/bulk', { data: { ids } });
  },

  moveFilesToFolder: (ids: string[], folderId: string | null) => {
    return api.patch('/files/bulk/move', { ids, folderId });
  },

  searchComparePP: (filters: Record<string, any>) => {
    const params = new URLSearchParams({ fields: filters.fields.join(',') }).toString();
    return api.get(`/treaty/compare-pp/search?${params}`);
  },
};
